import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Plus, Save, Trash2, ArrowLeft, Database, Building, MapPin, BookOpen, Users, Calendar, CreditCard as Edit3, AlertCircle, BarChart3, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { personalizedCollegeService, type CollegeWithBranches, type CollegeBranch, type CounselingRound } from '../lib/personalizedCollegeService';
import { useAuth } from '../contexts/AuthContext';

const BranchManager = () => {
  const { collegeId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  
  const [college, setCollege] = useState<CollegeWithBranches | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [showAddBranchForm, setShowAddBranchForm] = useState(false);
  const [editingBranchId, setEditingBranchId] = useState<string | null>(null);
  
  const [expandedBranches, setExpandedBranches] = useState<string[]>([]);
  const [showAddRoundForm, setShowAddRoundForm] = useState<string | null>(null);
  const [editingRoundId, setEditingRoundId] = useState<string | null>(null);
  
  const [newBranch, setNewBranch] = useState<Partial<CollegeBranch>>({
    branch_name: '',
    branch_code: '',
    cutoff_general: 0,
    cutoff_obc: 0,
    cutoff_sc: 0,
    cutoff_st: 0,
    cutoff_ews: 0,
    seats_available: 0,
    duration: '4 years',
    degree_type: 'B.Tech'
  });
  
  const [newRound, setNewRound] = useState<Partial<CounselingRound>>({
    round_number: 1,
    round_name: 'Round 1',
    year: new Date().getFullYear().toString(),
    cutoff_general: 0,
    cutoff_obc: 0,
    cutoff_sc: 0,
    cutoff_st: 0,
    cutoff_ews: 0,
    seats_filled: 0
  });

  useEffect(() => {
    if (isAdmin && collegeId) {
      fetchCollege();
    }
  }, [isAdmin, collegeId]);

  const fetchCollege = async () => {
    if (!collegeId) return;
    
    try {
      setLoading(true);
      const data = await personalizedCollegeService.getById(collegeId);
      setCollege(data);
    } catch (error) {
      console.error('Error fetching college:', error);
      toast.error('Failed to fetch college details');
      navigate('/manage-personalized-colleges');
    } finally {
      setLoading(false);
    }
  };

  const toggleBranchExpand = (branchId: string) => {
    setExpandedBranches(prev => 
      prev.includes(branchId) 
        ? prev.filter(id => id !== branchId) 
        : [...prev, branchId]
    );
  };

  const toInt = (value: string) => {
    const n = parseInt(value, 10);
    return isNaN(n) ? 0 : n;
  };

  const handleBranchInputChange = (field: string, value: any) => {
    setNewBranch(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRoundInputChange = (field: string, value: any) => {
    setNewRound(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetBranchForm = () => {
    setNewBranch({
      branch_name: '',
      branch_code: '',
      cutoff_general: 0,
      cutoff_obc: 0,
      cutoff_sc: 0,
      cutoff_st: 0,
      cutoff_ews: 0,
      seats_available: 0,
      duration: '4 years',
      degree_type: 'B.Tech'
    });
  };

  const resetRoundForm = () => {
    setNewRound({
      round_number: 1,
      round_name: 'Round 1',
      year: new Date().getFullYear().toString(),
      cutoff_general: 0,
      cutoff_obc: 0,
      cutoff_sc: 0,
      cutoff_st: 0,
      cutoff_ews: 0,
      seats_filled: 0
    });
  };

  const validateBranchForm = () => {
    if (!newBranch.branch_name?.trim()) {
      toast.error('Branch name is required');
      return false;
    }
    if (!newBranch.branch_code?.trim()) {
      toast.error('Branch code is required');
      return false;
    }
    if (!newBranch.seats_available || newBranch.seats_available <= 0) {
      toast.error('Seats available must be greater than 0');
      return false;
    }
    if (!newBranch.duration?.trim()) {
      toast.error('Duration is required');
      return false;
    }
    if (!newBranch.degree_type?.trim()) {
      toast.error('Degree type is required');
      return false;
    }
    return true;
  };

  const validateRoundForm = () => {
    if (!newRound.round_name?.trim()) {
      toast.error('Round name is required');
      return false;
    }
    if (!newRound.year?.trim()) {
      toast.error('Year is required');
      return false;
    }
    if (!newRound.round_number || newRound.round_number <= 0) {
      toast.error('Round number must be greater than 0');
      return false;
    }
    return true;
  };

  const saveBranch = async () => {
    if (!validateBranchForm() || !collegeId) return;

    setSaving(true);
    try {
      if (editingBranchId) {
        await personalizedCollegeService.updateBranch(editingBranchId, newBranch);
        toast.success('Branch updated successfully!');
        setEditingBranchId(null);
      } else {
        await personalizedCollegeService.createBranch({
          ...newBranch as CollegeBranch,
          college_id: collegeId
        });
        toast.success('Branch added successfully!');
      }
      
      setShowAddBranchForm(false);
      resetBranchForm();
      fetchCollege();
    } catch (error) {
      console.error('Error saving branch:', error);
      toast.error('Failed to save branch');
    } finally {
      setSaving(false);
    }
  };

  const saveRound = async () => {
    if (!validateRoundForm() || !showAddRoundForm) return;

    setSaving(true);
    try {
      if (editingRoundId) {
        await personalizedCollegeService.updateCounselingRound(editingRoundId, newRound);
        toast.success('Counseling round updated successfully!');
        setEditingRoundId(null);
      } else {
        await personalizedCollegeService.createCounselingRound({
          ...newRound as CounselingRound,
          branch_id: showAddRoundForm
        });
        toast.success('Counseling round added successfully!');
      }
      
      setShowAddRoundForm(null);
      resetRoundForm();
      fetchCollege();
    } catch (error) {
      console.error('Error saving counseling round:', error);
      toast.error('Failed to save counseling round');
    } finally {
      setSaving(false);
    }
  };

  const editBranch = (branch: CollegeBranch) => {
    setNewBranch({
      branch_name: branch.branch_name,
      branch_code: branch.branch_code,
      cutoff_general: branch.cutoff_general || 0,
      cutoff_obc: branch.cutoff_obc || 0,
      cutoff_sc: branch.cutoff_sc || 0,
      cutoff_st: branch.cutoff_st || 0,
      cutoff_ews: branch.cutoff_ews || 0,
      seats_available: branch.seats_available || 0,
      duration: branch.duration || '4 years',
      degree_type: branch.degree_type || 'B.Tech'
    });
    setEditingBranchId(branch.id);
    setShowAddBranchForm(true);
  };

  const editRound = (round: CounselingRound) => {
    setNewRound({
      round_number: round.round_number,
      round_name: round.round_name,
      year: round.year,
      cutoff_general: round.cutoff_general || 0,
      cutoff_obc: round.cutoff_obc || 0,
      cutoff_sc: round.cutoff_sc || 0,
      cutoff_st: round.cutoff_st || 0,
      cutoff_ews: round.cutoff_ews || 0,
      seats_filled: round.seats_filled || 0
    });
    setEditingRoundId(round.id);
    setShowAddRoundForm(round.branch_id);
  };

  const deleteBranch = async (branchId: string) => {
    if (!confirm('Are you sure you want to delete this branch? This will also delete all counseling rounds for this branch.')) return;

    try {
      await personalizedCollegeService.deleteBranch(branchId);
      toast.success('Branch deleted successfully!');
      fetchCollege();
    } catch (error) {
      console.error('Error deleting branch:', error);
      toast.error('Failed to delete branch');
    }
  };

  const deleteRound = async (roundId: string) => {
    if (!confirm('Are you sure you want to delete this counseling round?')) return;

    try {
      await personalizedCollegeService.deleteCounselingRound(roundId);
      toast.success('Counseling round deleted successfully!');
      fetchCollege();
    } catch (error) {
      console.error('Error deleting counseling round:', error);
      toast.error('Failed to delete counseling round');
    }
  };

  // Redirect if not admin
  if (!isAdmin) {
    return (
      <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
        <div className="container py-5">
          <div className="text-center">
            <div className="card border-0 shadow-lg mx-auto" style={{ maxWidth: '600px' }}>
              <div className="card-body p-5">
                <div className="mb-4">
                  <AlertCircle className="h-16 w-16 text-warning mx-auto mb-3" />
                </div>
                <h1 className="h2 text-dark mb-3">Access Restricted</h1>
                <p className="text-muted mb-4">
                  You need admin privileges to access the Branch Management system.
                </p>
                <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                  <Link to="/" className="btn btn-primary">
                    Back to Home
                  </Link>
                  <Link to="/contact" className="btn btn-outline-primary">
                    Contact Support
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading college details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!college) {
    return (
      <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
        <div className="container py-5">
          <div className="text-center">
            <div className="card border-0 shadow-lg mx-auto" style={{ maxWidth: '600px' }}>
              <div className="card-body p-5">
                <div className="mb-4">
                  <AlertCircle className="h-16 w-16 text-danger mx-auto mb-3" />
                </div>
                <h1 className="h2 text-dark mb-3">College Not Found</h1>
                <p className="text-muted mb-4">
                  The college you're looking for doesn't exist or may have been deleted.
                </p>
                <Link to="/manage-personalized-colleges" className="btn btn-primary">
                  Back to College Manager
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
      <div className="container py-4">
        {/* Header */}
        <div className="d-flex align-items-center mb-4">
          <Link
            to="/manage-personalized-colleges"
            className="btn btn-link text-primary text-decoration-none p-0 me-3"
          >
            <ArrowLeft size={20} />
          </Link>
          <div className="d-flex align-items-center gap-3">
            <div className="bg-primary rounded-3 p-3">
              <Building className="text-white" size={32} />
            </div>
            <div>
              <h1 className="h2 fw-bold text-dark mb-1">{college.name}</h1>
              <p className="text-muted mb-0">{college.location}, {college.state}</p>
            </div>
          </div>
        </div>

        {/* College Info Card */}
        <div className="card border-0 shadow-lg mb-4">
          <div className="card-body p-4">
            <div className="row g-4">
              <div className="col-md-3">
                <img
                  src={college.image_url}
                  alt={college.name}
                  className="img-fluid rounded"
                  style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                />
              </div>
              <div className="col-md-9">
                <div className="row g-3">
                  <div className="col-md-4">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <Building size={16} className="text-primary" />
                      <span className="fw-semibold text-dark">College Type</span>
                    </div>
                    <p className="text-muted">{college.college_type.replace('_', ' ')}</p>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <BookOpen size={16} className="text-primary" />
                      <span className="fw-semibold text-dark">Exam Type</span>
                    </div>
                    <p className="text-muted">{college.exam_type.replace('_', ' ')}</p>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <MapPin size={16} className="text-primary" />
                      <span className="fw-semibold text-dark">Location</span>
                    </div>
                    <p className="text-muted">{college.location}, {college.state}</p>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <Users size={16} className="text-primary" />
                      <span className="fw-semibold text-dark">Total Seats</span>
                    </div>
                    <p className="text-muted">{college.seats_total}</p>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <BarChart3 size={16} className="text-primary" />
                      <span className="fw-semibold text-dark">NIRF Ranking</span>
                    </div>
                    <p className="text-muted">#{college.nirf_ranking || 'N/A'}</p>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <Calendar size={16} className="text-primary" />
                      <span className="fw-semibold text-dark">Established</span>
                    </div>
                    <p className="text-muted">{college.established}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Branches & Counseling Rounds */}
        <div className="card border-0 shadow-lg mb-4">
          <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Branches & Counseling Rounds</h5>
            <button
              onClick={() => {
                resetBranchForm();
                setShowAddBranchForm(true);
                setEditingBranchId(null);
              }}
              className="btn btn-light btn-sm d-flex align-items-center gap-2"
            >
              <Plus size={14} />
              Add Branch
            </button>
          </div>
          <div className="card-body p-4">
            {/* Add/Edit Branch Form */}
            {showAddBranchForm && (
              <motion.div 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card border mb-4"
              >
                <div className="card-header bg-light">
                  <h6 className="mb-0">{editingBranchId ? 'Edit Branch' : 'Add New Branch'}</h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Branch Name *</label>
                      <input
                        type="text"
                        value={newBranch.branch_name}
                        onChange={(e) => handleBranchInputChange('branch_name', e.target.value)}
                        className="form-control"
                        placeholder="e.g., Computer Science Engineering"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Branch Code *</label>
                      <input
                        type="text"
                        value={newBranch.branch_code}
                        onChange={(e) => handleBranchInputChange('branch_code', e.target.value)}
                        className="form-control"
                        placeholder="e.g., CSE"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Seats Available *</label>
                      <input
                        type="number"
                        value={newBranch.seats_available}
                        onChange={(e) => handleBranchInputChange('seats_available', toInt(e.target.value))}
                        className="form-control"
                        placeholder="Enter available seats"
                        min="0"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Duration *</label>
                      <input
                        type="text"
                        value={newBranch.duration}
                        onChange={(e) => handleBranchInputChange('duration', e.target.value)}
                        className="form-control"
                        placeholder="e.g., 4 years"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Degree Type *</label>
                      <input
                        type="text"
                        value={newBranch.degree_type}
                        onChange={(e) => handleBranchInputChange('degree_type', e.target.value)}
                        className="form-control"
                        placeholder="e.g., B.Tech"
                      />
                    </div>

                    <div className="col-12">
                      <h6 className="fw-semibold text-dark mb-3">Cutoff Information</h6>
                      <div className="row g-3">
                        <div className="col-md-4">
                          <label className="form-label fw-semibold">General Cutoff</label>
                          <input
                            type="number"
                            value={newBranch.cutoff_general}
                            onChange={(e) => handleBranchInputChange('cutoff_general', toInt(e.target.value))}
                            className="form-control"
                            placeholder="Enter General cutoff"
                            min="0"
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label fw-semibold">OBC Cutoff</label>
                          <input
                            type="number"
                            value={newBranch.cutoff_obc}
                            onChange={(e) => handleBranchInputChange('cutoff_obc', toInt(e.target.value))}
                            className="form-control"
                            placeholder="Enter OBC cutoff"
                            min="0"
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label fw-semibold">SC Cutoff</label>
                          <input
                            type="number"
                            value={newBranch.cutoff_sc}
                            onChange={(e) => handleBranchInputChange('cutoff_sc', toInt(e.target.value))}
                            className="form-control"
                            placeholder="Enter SC cutoff"
                            min="0"
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label fw-semibold">ST Cutoff</label>
                          <input
                            type="number"
                            value={newBranch.cutoff_st}
                            onChange={(e) => handleBranchInputChange('cutoff_st', toInt(e.target.value))}
                            className="form-control"
                            placeholder="Enter ST cutoff"
                            min="0"
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label fw-semibold">EWS Cutoff</label>
                          <input
                            type="number"
                            value={newBranch.cutoff_ews}
                            onChange={(e) => handleBranchInputChange('cutoff_ews', toInt(e.target.value))}
                            className="form-control"
                            placeholder="Enter EWS cutoff"
                            min="0"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="d-flex gap-2 mt-4">
                    <button
                      onClick={saveBranch}
                      disabled={saving}
                      className="btn btn-primary d-flex align-items-center gap-2"
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save size={16} />
                          {editingBranchId ? 'Update Branch' : 'Save Branch'}
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setShowAddBranchForm(false);
                        setEditingBranchId(null);
                        resetBranchForm();
                      }}
                      className="btn btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Branches List */}
            {college.branches && college.branches.length > 0 ? (
              <div className="vstack gap-4">
                {college.branches.map((branch) => (
                  <div key={branch.id} className="card border">
                    <div 
                      className="card-header bg-light d-flex justify-content-between align-items-center"
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleBranchExpand(branch.id)}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <h6 className="mb-0 fw-bold">{branch.branch_name} ({branch.branch_code})</h6>
                        <span className="badge bg-primary">{branch.degree_type}</span>
                        <span className="badge bg-secondary">{branch.duration}</span>
                        <span className="badge bg-success">{branch.seats_available} seats</span>
                      </div>
                      <div className="d-flex gap-3 align-items-center">
                        <div className="d-flex gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              editBranch(branch);
                            }}
                            className="btn btn-sm btn-outline-primary"
                          >
                            <Edit3 size={14} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteBranch(branch.id);
                            }}
                            className="btn btn-sm btn-outline-danger"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        {expandedBranches.includes(branch.id) ? (
                          <ChevronUp size={16} />
                        ) : (
                          <ChevronDown size={16} />
                        )}
                      </div>
                    </div>
                    
                    {expandedBranches.includes(branch.id) && (
                      <div className="card-body">
                        {/* Branch Cutoffs */}
                        <div className="mb-4">
                          <h6 className="fw-semibold text-dark mb-3">Branch Cutoffs</h6>
                          <div className="table-responsive">
                            <table className="table table-bordered">
                              <thead className="table-light">
                                <tr>
                                  <th>General</th>
                                  <th>OBC</th>
                                  <th>SC</th>
                                  <th>ST</th>
                                  <th>EWS</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td>{branch.cutoff_general || 'N/A'}</td>
                                  <td>{branch.cutoff_obc || 'N/A'}</td>
                                  <td>{branch.cutoff_sc || 'N/A'}</td>
                                  <td>{branch.cutoff_st || 'N/A'}</td>
                                  <td>{branch.cutoff_ews || 'N/A'}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Counseling Rounds */}
                        <div>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="fw-semibold text-dark mb-0">Counseling Rounds</h6>
                            <button
                              onClick={() => {
                                resetRoundForm();
                                setShowAddRoundForm(branch.id);
                                setEditingRoundId(null);
                              }}
                              className="btn btn-sm btn-success d-flex align-items-center gap-2"
                            >
                              <Plus size={14} />
                              Add Round
                            </button>
                          </div>

                          {/* Add/Edit Round Form */}
                          {showAddRoundForm === branch.id && (
                            <motion.div 
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="card border mb-3"
                            >
                              <div className="card-header bg-light">
                                <h6 className="mb-0">{editingRoundId ? 'Edit Counseling Round' : 'Add New Counseling Round'}</h6>
                              </div>
                              <div className="card-body">
                                <div className="row g-3">
                                  <div className="col-md-4">
                                    <label className="form-label fw-semibold">Round Number *</label>
                                    <input
                                      type="number"
                                      value={newRound.round_number}
                                      onChange={(e) => handleRoundInputChange('round_number', toInt(e.target.value))}
                                      className="form-control"
                                      placeholder="e.g., 1"
                                      min="1"
                                    />
                                  </div>
                                  <div className="col-md-4">
                                    <label className="form-label fw-semibold">Round Name *</label>
                                    <input
                                      type="text"
                                      value={newRound.round_name}
                                      onChange={(e) => handleRoundInputChange('round_name', e.target.value)}
                                      className="form-control"
                                      placeholder="e.g., Round 1"
                                    />
                                  </div>
                                  <div className="col-md-4">
                                    <label className="form-label fw-semibold">Year *</label>
                                    <input
                                      type="text"
                                      value={newRound.year}
                                      onChange={(e) => handleRoundInputChange('year', e.target.value)}
                                      className="form-control"
                                      placeholder="e.g., 2025"
                                    />
                                  </div>
                                  <div className="col-md-4">
                                    <label className="form-label fw-semibold">Seats Filled</label>
                                    <input
                                      type="number"
                                      value={newRound.seats_filled}
                                      onChange={(e) => handleRoundInputChange('seats_filled', toInt(e.target.value))}
                                      className="form-control"
                                      placeholder="Enter seats filled"
                                      min="0"
                                    />
                                  </div>

                                  <div className="col-12">
                                    <h6 className="fw-semibold text-dark mb-3">Round Cutoffs</h6>
                                    <div className="row g-3">
                                      <div className="col-md-4">
                                        <label className="form-label fw-semibold">General Cutoff</label>
                                        <input
                                          type="number"
                                          value={newRound.cutoff_general}
                                          onChange={(e) => handleRoundInputChange('cutoff_general', toInt(e.target.value))}
                                          className="form-control"
                                          placeholder="Enter General cutoff"
                                          min="0"
                                        />
                                      </div>
                                      <div className="col-md-4">
                                        <label className="form-label fw-semibold">OBC Cutoff</label>
                                        <input
                                          type="number"
                                          value={newRound.cutoff_obc}
                                          onChange={(e) => handleRoundInputChange('cutoff_obc', toInt(e.target.value))}
                                          className="form-control"
                                          placeholder="Enter OBC cutoff"
                                          min="0"
                                        />
                                      </div>
                                      <div className="col-md-4">
                                        <label className="form-label fw-semibold">SC Cutoff</label>
                                        <input
                                          type="number"
                                          value={newRound.cutoff_sc}
                                          onChange={(e) => handleRoundInputChange('cutoff_sc', toInt(e.target.value))}
                                          className="form-control"
                                          placeholder="Enter SC cutoff"
                                          min="0"
                                        />
                                      </div>
                                      <div className="col-md-4">
                                        <label className="form-label fw-semibold">ST Cutoff</label>
                                        <input
                                          type="number"
                                          value={newRound.cutoff_st}
                                          onChange={(e) => handleRoundInputChange('cutoff_st', toInt(e.target.value))}
                                          className="form-control"
                                          placeholder="Enter ST cutoff"
                                          min="0"
                                        />
                                      </div>
                                      <div className="col-md-4">
                                        <label className="form-label fw-semibold">EWS Cutoff</label>
                                        <input
                                          type="number"
                                          value={newRound.cutoff_ews}
                                          onChange={(e) => handleRoundInputChange('cutoff_ews', toInt(e.target.value))}
                                          className="form-control"
                                          placeholder="Enter EWS cutoff"
                                          min="0"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="d-flex gap-2 mt-4">
                                  <button
                                    onClick={saveRound}
                                    disabled={saving}
                                    className="btn btn-primary d-flex align-items-center gap-2"
                                  >
                                    {saving ? (
                                      <>
                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                        Saving...
                                      </>
                                    ) : (
                                      <>
                                        <Save size={16} />
                                        {editingRoundId ? 'Update Round' : 'Save Round'}
                                      </>
                                    )}
                                  </button>
                                  <button
                                    onClick={() => {
                                      setShowAddRoundForm(null);
                                      setEditingRoundId(null);
                                      resetRoundForm();
                                    }}
                                    className="btn btn-secondary"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            </motion.div>
                          )}

                          {/* Rounds Table */}
                          {branch.counseling_rounds && branch.counseling_rounds.length > 0 ? (
                            <div className="table-responsive">
                              <table className="table table-bordered table-hover">
                                <thead className="table-light">
                                  <tr>
                                    <th>Round</th>
                                    <th>Year</th>
                                    <th>General</th>
                                    <th>OBC</th>
                                    <th>SC</th>
                                    <th>ST</th>
                                    <th>EWS</th>
                                    <th>Seats Filled</th>
                                    <th>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {branch.counseling_rounds.sort((a, b) => {
                                    // Sort by year (descending) and then by round number (ascending)
                                    if (a.year !== b.year) {
                                      return b.year.localeCompare(a.year);
                                    }
                                    return a.round_number - b.round_number;
                                  }).map((round) => (
                                    <tr key={round.id}>
                                      <td>{round.round_name}</td>
                                      <td>{round.year}</td>
                                      <td>{round.cutoff_general || 'N/A'}</td>
                                      <td>{round.cutoff_obc || 'N/A'}</td>
                                      <td>{round.cutoff_sc || 'N/A'}</td>
                                      <td>{round.cutoff_st || 'N/A'}</td>
                                      <td>{round.cutoff_ews || 'N/A'}</td>
                                      <td>{round.seats_filled || 0}</td>
                                      <td>
                                        <div className="d-flex gap-2">
                                          <button
                                            onClick={() => editRound(round)}
                                            className="btn btn-sm btn-outline-primary"
                                          >
                                            <Edit3 size={14} />
                                          </button>
                                          <button
                                            onClick={() => deleteRound(round.id)}
                                            className="btn btn-sm btn-outline-danger"
                                          >
                                            <Trash2 size={14} />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="alert alert-info d-flex align-items-center gap-2">
                              <Clock size={16} />
                              <span>No counseling rounds added yet. Add rounds to track cutoff history.</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-5">
                <div className="mb-4">
                  <BookOpen className="text-muted mx-auto mb-3" size={64} />
                </div>
                <h3 className="h4 text-dark mb-3">No branches found</h3>
                <p className="text-muted mb-4">
                  This college doesn't have any branches yet. Add branches to manage cutoffs and counseling rounds.
                </p>
                <button
                  onClick={() => {
                    resetBranchForm();
                    setShowAddBranchForm(true);
                    setEditingBranchId(null);
                  }}
                  className="btn btn-primary"
                >
                  Add Your First Branch
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BranchManager;